/**
 * 消息服务
 * 统一消息收发操作
 */
import { getTIM } from '../sdk/tim/index.js'
import { eventBus, SDK_EVENTS } from '../sdk/eventBus.js'
import TencentCloudChat from '@tencentcloud/chat'
import { SDK_ERRORS } from '../errors/codes.js'
import { wrapSDKError, handleSDKError } from '../errors/handler.js'
import { logger } from '../utils/logger.js'

const log = logger.module('MessageService')

/**
 * 初始化消息相关事件监听
 * 需在 TIM SDK ready 后调用
 */
export function initMessageListener() {
  const tim = getTIM()
  if (!tim) {
    log.warn('TIM not initialized, skip listener init')
    return
  }

  const Event = TencentCloudChat.EVENT

  // 新消息
  tim.on(Event.MESSAGE_RECEIVED, (event) => {
    log.debug('MESSAGE_RECEIVED, count:', event.data?.length || 0)
    eventBus.emit(SDK_EVENTS.MESSAGE_RECEIVED, event.data || [])
  })

  // 消息已读
  tim.on(Event.MESSAGE_READ_BY_PEER, (event) => {
    eventBus.emit(SDK_EVENTS.MESSAGE_READ_BY_PEER, event.data)
  })

  // 消息撤回
  tim.on(Event.MESSAGE_REVOKED, (event) => {
    eventBus.emit(SDK_EVENTS.MESSAGE_REVOKED, event.data || [])
  })
}

/**
 * 获取会话消息列表
 * @param {string} conversationID
 * @param {string} nextReqMessageID
 * @param {number} count
 */
export async function getMessageList(conversationID, nextReqMessageID = '', count = 20) {
  const tim = getTIM()
  if (!tim) {
    const err = wrapSDKError(SDK_ERRORS.TIM_NOT_INIT, null, '获取消息列表失败：SDK未初始化')
    log.error(err)
    throw err
  }
  if(!conversationID) return []
  const params = { conversationID, count }
  if (nextReqMessageID) {
    params.nextReqMessageID = nextReqMessageID
  }

  try {
    const res = await tim.getMessageList(params)
    return res.data
  } catch (err) {
    const appErr = wrapSDKError(SDK_ERRORS.TIM_GET_MSG_FAILED, err, `获取消息列表失败：${err.message}`)
    handleSDKError(err, SDK_ERRORS.TIM_GET_MSG_FAILED, { conversationID })
    throw appErr
  }
}

/**
 * 发送文本消息
 * @param {string} conversationID
 * @param {string} text
 */
export async function sendTextMessage(conversationID, text) {
  const tim = getTIM()
  if (!tim) {
    const err = wrapSDKError(SDK_ERRORS.TIM_NOT_INIT, null, '发送消息失败：SDK未初始化')
    throw err
  }

  try {
    const msg = tim.createTextMessage({
      to: _normalizeToID(conversationID),
      conversationType: TencentCloudChat.TYPES.CONV_C2C,
      payload: { text }
    })

    const res = await tim.sendMessage(msg)
    return res.data.message
  } catch (err) {
    const appErr = wrapSDKError(SDK_ERRORS.TIM_SEND_MSG_FAILED, err, `发送消息失败：${err.message}`)
    handleSDKError(err, SDK_ERRORS.TIM_SEND_MSG_FAILED, { conversationID })
    throw appErr
  }
}

/**
 * 发送图片消息
 * @param {string} conversationID
 * @param {File} file
 */
export async function sendImageMessage(conversationID, file) {
  const tim = getTIM()
  if (!tim) {
    const err = wrapSDKError(SDK_ERRORS.TIM_NOT_INIT, null, '发送图片失败：SDK未初始化')
    throw err
  }

  try {
    const msg = tim.createImageMessage({
      to: _normalizeToID(conversationID),
      conversationType: TencentCloudChat.TYPES.CONV_C2C,
      payload: { file }
    })

    const res = await tim.sendMessage(msg)
    return res.data.message
  } catch (err) {
    const appErr = wrapSDKError(SDK_ERRORS.TIM_SEND_MSG_FAILED, err, `发送图片失败：${err.message}`)
    handleSDKError(err, SDK_ERRORS.TIM_SEND_MSG_FAILED, { conversationID })
    throw appErr
  }
}

/**
 * 发送语音消息
 * @param {string} conversationID
 * @param {Blob} audioBlob
 * @param {number} duration
 */
export async function sendAudioMessage(conversationID, audioBlob, duration) {
  const tim = getTIM()
  if (!tim) {
    const err = wrapSDKError(SDK_ERRORS.TIM_NOT_INIT, null, '发送语音失败：SDK未初始化')
    throw err
  }

  try {
    const file = new File([audioBlob], 'voice.mp3', { type: audioBlob.type })

    const msg = tim.createAudioMessage({
      to: _normalizeToID(conversationID),
      conversationType: TencentCloudChat.TYPES.CONV_C2C,
      payload: { file }
    })

    const res = await tim.sendMessage(msg)
    return res.data.message
  } catch (err) {
    const appErr = wrapSDKError(SDK_ERRORS.TIM_SEND_MSG_FAILED, err, `发送语音失败：${err.message}`)
    handleSDKError(err, SDK_ERRORS.TIM_SEND_MSG_FAILED, { conversationID })
    throw appErr
  }
}

/**
 * 发送自定义消息
 * @param {string} conversationID
 * @param {string} data
 * @param {string} description
 * @param {string} extension
 */
export async function sendCustomMessage(conversationID, data, description = '', extension = '') {
  const tim = getTIM()
  if (!tim) {
    const err = wrapSDKError(SDK_ERRORS.TIM_NOT_INIT, null, '发送自定义消息失败：SDK未初始化')
    throw err
  }
  try {
    const msg = tim.createCustomMessage({
      to: _normalizeToID(conversationID),
      conversationType: TencentCloudChat.TYPES.CONV_C2C,
      payload: { data, description, extension }
    })

    const res = await tim.sendMessage(msg)
    return res.data.message
  } catch (err) {
    const appErr = wrapSDKError(SDK_ERRORS.TIM_SEND_MSG_FAILED, err, `发送自定义消息失败：${err.message}`)
    handleSDKError(err, SDK_ERRORS.TIM_SEND_MSG_FAILED, { conversationID })
    throw appErr
  }
}

/**
 * 撤回消息
 * @param {Object} message
 */
export async function revokeMessage(message) {
  const tim = getTIM()
  if (!tim) {
    const err = wrapSDKError(SDK_ERRORS.TIM_NOT_INIT, null, '撤回消息失败：SDK未初始化')
    throw err
  }

  try {
    return await tim.revokeMessage(message)
  } catch (err) {
    const appErr = wrapSDKError(SDK_ERRORS.TIM_SEND_MSG_FAILED, err, `撤回消息失败：${err.message}`)
    handleSDKError(err, SDK_ERRORS.TIM_SEND_MSG_FAILED)
    throw appErr
  }
}

/**
 * 重发消息
 * @param {Object} message
 */
export async function resendMessage(message) {
  const tim = getTIM()
  if (!tim) {
    const err = wrapSDKError(SDK_ERRORS.TIM_NOT_INIT, null, '重发消息失败：SDK未初始化')
    throw err
  }

  try {
    return await tim.resendMessage(message)
  } catch (err) {
    const appErr = wrapSDKError(SDK_ERRORS.TIM_SEND_MSG_FAILED, err, `重发消息失败：${err.message}`)
    handleSDKError(err, SDK_ERRORS.TIM_SEND_MSG_FAILED)
    throw appErr
  }
}

/**
 * 标记会话已读
 * @param {string} conversationID
 */
export async function setMessageRead(conversationID) {
  const tim = getTIM()
  if (!tim) {
    log.warn('TIM not ready, skip setMessageRead')
    return
  }

  if (!conversationID) {
    log.warn('conversationID is empty, skip setMessageRead')
    return
  }

  try {
    return await tim.setMessageRead({ conversationID })
  } catch (err) {
    log.warn('setMessageRead failed:', err.message)
    // 不抛出错误，静默处理
  }
}

/**
 * 获取会话资料
 * @param {string} conversationID
 */
export async function getConversationProfile(conversationID) {
  const tim = getTIM()
  if (!tim) return null

  try {
    const res = await tim.getConversationProfile(conversationID)
    return res.data.conversation
  } catch (err) {
    log.warn('getConversationProfile failed:', err.message)
    return null
  }
}

// ============ 私有方法 ============

/**
 * 标准化 To ID（去除 C2C 前缀）
 */
function _normalizeToID(conversationID) {
  return String(conversationID).replace(/^C2C/, '')
}

export default {
  initMessageListener,
  getMessageList,
  sendTextMessage,
  sendImageMessage,
  sendAudioMessage,
  sendCustomMessage,
  revokeMessage,
  resendMessage,
  setMessageRead,
  getConversationProfile
}
